import type { IsoProjection } from '../core/iso';

/**
 * Rendering order for the whole world, in one place.
 *
 * Fixed layers (always negative) sit below everything that stands on the map.
 * Standing objects (trees, banners — later buildings and units) use
 * `objectDepth`, which is their base world Y plus tiny deterministic
 * tie-breakers, so anything lower on screen (nearer the viewer) is drawn in
 * front. The top overlay is above every possible object depth.
 */
export const LAYER = {
  skirt: -5_000_000,
  ground: -4_000_000,
  /** Passability / future placement tint drawn on the ground. */
  groundOverlay: -3_500_000,
  grid: -3_000_000,
  /** Flat decals lying on the ground (flowers). */
  groundDecal: -2_500_000,
  boundary: -2_000_000,
  /** Hover / selection fill on the ground, hidden behind tall objects. */
  markers: -1_000_000,
  /** Selection / hover outline drawn above everything so it is never lost behind trees. */
  overlay: 1_000_000_000,
} as const;

/** Largest grid coordinate the tie-breaker supports without breaking row order. */
export const MAX_GRID_FOR_DEPTH = 1000;

/**
 * Draw depth for an object standing on tile (gx, gy).
 * `bias` (−100..100) orders different objects on the same tile, e.g. a unit
 * standing in front of a building's base.
 */
export function objectDepth(iso: IsoProjection, gx: number, gy: number, bias = 0): number {
  const y = iso.gridToWorld(gx, gy).y;
  return y + (gx / MAX_GRID_FOR_DEPTH) * 0.5 + Math.max(-100, Math.min(100, bias)) * 1e-5;
}
